import React from 'react';
import { Box, Button } from '@material-ui/core';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import PersonIcon from '@material-ui/icons/Person';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import SettingsIcon from '@material-ui/icons/Settings';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getIntlPath } from 'common/utils';
import { UserAccountDisplay } from 'user/components/UserAccountDisplay';
import { useAuth } from 'auth/hooks';

import { Link } from '../Link';
import { Logo } from '../Logo/Logo';
import { ButtonLink } from '../buttons/ButtonLink';

interface IProps {
    className?: string;
    onClose(): void;
}

const BaseNavigationContent = ({ className, onClose }: IProps) => {
    const { t } = useTranslation();

    const location = useLocation();
    const history = useHistory();
    const { logout } = useAuth();
    const { pathname } = location;

    const isInvitationsOverview = pathname === getIntlPath('invitations');
    const isNewEmployee = pathname === getIntlPath('newEmployee');
    const isAccount = pathname === getIntlPath('account');

    const handleLogout = () => {
        logout();

        history.push(getIntlPath('login'));
    };

    return (
        <Box className={className}>
            <Box className="mobileTopSection">
                <Logo />

                <Button className="closeButton" onClick={onClose}>
                    <CloseOutlinedIcon />
                </Button>
            </Box>
            <Box>
                <Link
                    className={isInvitationsOverview ? 'active' : ''}
                    to={getIntlPath('invitations')}
                >
                    {isInvitationsOverview ? (
                        <PersonIcon />
                    ) : (
                        <PersonOutlineOutlinedIcon />
                    )}{' '}
                    {t('navigation.employees.main')}
                </Link>
            </Box>
            <Box className="subNavigation">
                <Link
                    className={isInvitationsOverview ? 'active' : ''}
                    to={getIntlPath('invitations')}
                >
                    {t('navigation.employees.overview')}
                </Link>
                <Link
                    className={isNewEmployee ? 'active' : ''}
                    to={getIntlPath('newEmployee')}
                >
                    {t('navigation.employees.new')}
                </Link>
            </Box>

            <Box>
                <Link
                    hash
                    className={isAccount ? 'active' : ''}
                    to={`${getIntlPath('account')}#`}
                >
                    {isAccount ? <SettingsIcon /> : <SettingsOutlinedIcon />}{' '}
                    {t('navigation.account.main')}
                </Link>
                <Box className="subNavigation">
                    <Link
                        hash
                        className={
                            location.hash === '#administrators' ? 'active' : ''
                        }
                        to={`${getIntlPath('account')}#administrators`}
                    >
                        {t('navigation.account.administrators')}
                    </Link>
                    <Link
                        hash
                        className={
                            location.hash === '#companyinformation'
                                ? 'active'
                                : ''
                        }
                        to={`${getIntlPath('account')}#companyinformation`}
                    >
                        {t('navigation.account.company')}
                    </Link>
                    <Link
                        hash
                        className={location.hash === '#budget' ? 'active' : ''}
                        to={`${getIntlPath('account')}#budget`}
                    >
                        {t('navigation.account.budget')}
                    </Link>
                </Box>
            </Box>

            <Box className="logoutButtonWrapper" ml={10} mt={6}>
                <ButtonLink onClick={handleLogout}>
                    {t('navigation.account.logout')}
                </ButtonLink>
            </Box>

            <UserAccountDisplay />
        </Box>
    );
};

export const NavigationContent = styled(BaseNavigationContent)`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.palette.background.paper};
    position: fixed;
    top: 0;
    right: -100%;
    padding: ${({ theme }) => `0 ${theme.spacing(5)}px ${theme.spacing(8)}px`};
    transition: right 250ms linear;
    z-index: 100;

    .mobileTopSection {
        display: flex;
        height: 80px;
        align-items: center;
        margin-bottom: ${({ theme }) => `${theme.spacing(8)}px`};

        ${Logo} {
            height: 100%;

            .logoWrapper {
                width: 54px;
                margin-right: ${({ theme }) => `${theme.spacing(1)}px`};
            }

            .MuiTypography-root {
                font-size: ${({ theme }) => theme.typography.body2.fontSize};
                line-height: 1;
            }

            svg {
                height: 36px;
            }
        }

        .closeButton {
            margin-left: auto;
            color: ${({ theme }) => theme.palette.primary.main};

            svg {
                width: 40px;
                height: 40px;
            }
        }
    }

    ${Link} {
        display: flex;
        align-items: center;
        padding: ${({ theme }) => `${theme.spacing(3)}px 0`};
        text-decoration: none;
        color: ${({ theme }) => theme.palette.text.secondary};

        :hover {
            color: ${({ theme }) => theme.palette.primary.main};
            font-weight: bold;
        }

        &.active {
            color: ${({ theme }) => theme.palette.primary.main};
            font-weight: bold;
        }

        .MuiSvgIcon-root {
            position: relative;
            top: -2px;
            font-size: 1.5rem;
            margin-right: ${({ theme }) => `${theme.spacing(5)}px`};
        }
    }

    .subNavigation {
        margin-left: ${({ theme }) => `${theme.spacing(13)}px`};

        ${Link} {
            padding: ${({ theme }) => `${theme.spacing(2)}px 0`};
        }
    }

    ${ButtonLink} {
        .MuiButton-label {
            font-weight: 400;
            color: ${({ theme }) => theme.palette.text.secondary};
        }

        :hover {
            text-decoration: none;

            .MuiButton-label {
                color: ${({ theme }) => theme.palette.primary.main};
                font-weight: bold;
            }
        }
    }

    ${UserAccountDisplay} {
        display: none;
        margin-top: auto;
        align-self: flex-start;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['md']}px`}) {
        position: relative;
        left: 0;
        right: auto;
        padding: 0;

        .mobileTopSection {
            display: none;
        }

        .logoutButtonWrapper {
            display: none;
        }

        ${UserAccountDisplay} {
            display: block;
        }
    }
`;
